<template>
  <div class="house-check-container app-page app-page--fill">
    <el-card>
      <div class="main-content" ref="mainContentRef">
        <!-- 左侧房屋体检定义列表 -->
        <div class="left-panel" ref="leftPanelRef" :style="{ width: leftPanelWidth }">
          <div class="list-header">
            <el-button type="primary" @click="addNewCheck">
              <el-icon>
                <Plus />
              </el-icon>
              新增
            </el-button>
            <el-button
              type="primary"
              circle
              size="small"
              class="app-refresh-btn"
              @click="loadCheckList"
              :loading="listLoading"
              :icon="Refresh"
              style="margin-left: auto;"
              title="刷新"
            />
          </div>

          <div class="check-list-wrapper">
            <DynamicTable
              :data="allCheckList"
              :model="checkTableModel"
              :column-labels="checkTableLabels"
              :loading="listLoading"
              :show-column-selector="false"
              :show-export="false"
              :show-edit-button="true"
              :show-delete-button="true"
              :pagination="checkPagination"
              :active-row-key="activeCheckRowKey"
              @edit="handleCheckEdit"
              @delete="handleCheckDelete"
              @filter="handleFilter"
              @page-change="handleCheckPageChange"
              @page-size-change="handleCheckPageSizeChange"
            />
          </div>
        </div>

        <!-- 拖拽分隔条 -->
        <div class="resize-handle" @mousedown="startResize"></div>

        <!-- 右侧体检定义详情：名称输入 + 页签动态表单设计器 -->
        <div class="right-panel" v-loading="detailLoading" element-loading-text="加载中...">
          <div v-if="currentCheck" class="edit-area">
            <!-- 编辑模式控制：顶层操作控制栏 -->
            <div class="edit-mode-control">
              <div class="left-actions">
                <el-button v-if="!isEditMode" type="primary" :icon="Edit" @click="enterEditMode">
                  编辑
                </el-button>
                <el-button v-if="isEditMode" :icon="Close" @click="exitEditMode">
                  取消编辑
                </el-button>
              </div>
              <div v-if="isEditMode" class="right-actions">
                <el-button type="primary" :icon="DocumentChecked" @click="saveCheckData" :loading="saveLoading">保存数据</el-button>
                <el-button :icon="RefreshLeft" @click="resetCurrentCheck" :disabled="saveLoading">重置</el-button>
              </div>
            </div>

            <el-form
              ref="checkFormRef"
              :model="currentCheck"
              :rules="checkFormRules"
              label-width="120px"
              class="form-content"
            >
              <!-- 基本信息区域：独占一行（为未来扩展更多基本信息预留区域） -->
              <div class="basic-info-section">
                <el-form-item class="check-name-item" label="体检报告名称" prop="name">
                  <el-input 
                    v-model="currentCheck.name" 
                    placeholder="请输入房屋体检报告名称" 
                    :readonly="!isEditMode"
                    style="width: 100%; max-width: 500px;"
                  />
                </el-form-item>
              </div>

              <!-- 页签切换功能：使用数组循环渲染 -->
              <div class="tabs-container">
                <el-tabs v-model="activeTab" type="border-card" class="design-tabs">
                  <el-tab-pane
                    v-for="tab in tabList"
                    :key="tab.key"
                    :label="tab.label"
                    :name="tab.key"
                  >
                    <!-- 页签内的动态表单设计器 -->
                    <div class="tab-designer">
                      <!-- 工具栏：仅在编辑模式下显示 -->
                      <div class="designer-toolbar" v-if="isEditMode">
                        <el-button type="success" :icon="Plus" size="small" @click="openAddElementDialog(tab.key)">
                          添加表单元素
                        </el-button>
                        <span class="toolbar-tip">可以在表单项右侧对其进行移动、修改或删除</span>
                      </div>

                      <!-- 表单元素列表展示与预览 -->
                      <div class="elements-list">
                        <div v-if="!getCurrentTabElements(tab.key).length" class="empty-elements">
                          <el-empty :description="isEditMode ? '暂无表单元素，请点击上方按钮添加' : '查看模式下，当前页签暂无表单元素'" :image-size="80" />
                        </div>
                        
                        <div 
                          v-for="(element, index) in getCurrentTabElements(tab.key)" 
                          :key="element.id" 
                          class="element-item-card"
                          :class="{ 
                            'is-edit-mode': isEditMode,
                            'is-textarea': element.type === 'textarea'
                          }"
                        >
                          <div class="element-preview-area">
                            <span class="element-label">{{ element.name }}</span>
                            
                            <!-- 根据表单元素类型进行真实的 UI 渲染 -->
                            <div class="element-control-wrapper">
                              <!-- 文本输入框 -->
                              <el-input 
                                v-if="element.type === 'text'" 
                                :placeholder="element.placeholder || '请输入文本'"
                                readonly
                                class="preview-readonly"
                              />
                              
                              <!-- 数字输入框 -->
                              <el-input-number 
                                v-else-if="element.type === 'number'" 
                                :model-value="undefined"
                                :placeholder="element.placeholder || '请输入数字'"
                                disabled
                                controls-position="right"
                                style="width: 100%; max-width: 220px;"
                                class="preview-number"
                              />
                              
                              <!-- 单选选择框 -->
                              <el-radio-group 
                                v-else-if="element.type === 'radio'" 
                                disabled
                              >
                                <el-radio 
                                  v-for="opt in element.options" 
                                  :key="opt" 
                                  :label="opt"
                                >
                                  {{ opt }}
                                </el-radio>
                              </el-radio-group>
                              
                              <!-- 多选选择框 -->
                              <div v-else-if="element.type === 'checkbox'" style="width: 100%;">
                                <!-- 情况一：配置了可选项目统计，按必检与可选对照区分渲染 -->
                                <div v-if="element.optionalOptions && element.optionalOptions.length > 0" class="checkbox-split-view">
                                  <div class="split-column required-column">
                                    <div class="column-header">必检项目统计</div>
                                    <div class="column-body">
                                      <el-checkbox-group disabled>
                                        <el-checkbox 
                                          v-for="opt in element.options" 
                                          :key="opt" 
                                          :label="opt"
                                        >
                                          {{ opt }}
                                        </el-checkbox>
                                      </el-checkbox-group>
                                    </div>
                                  </div>
                                  <div class="split-column optional-column">
                                    <div class="column-header">可选项目统计</div>
                                    <div class="column-body">
                                      <el-checkbox-group disabled>
                                        <el-checkbox 
                                          v-for="opt in element.optionalOptions" 
                                          :key="opt" 
                                          :label="opt"
                                        >
                                          {{ opt }}
                                        </el-checkbox>
                                      </el-checkbox-group>
                                    </div>
                                  </div>
                                </div>
                                
                                <!-- 情况二：未配置可选项目统计，只单独平铺显示多选选项 -->
                                <el-checkbox-group 
                                  v-else
                                  disabled
                                >
                                  <el-checkbox 
                                    v-for="opt in element.options" 
                                    :key="opt" 
                                    :label="opt"
                                  >
                                    {{ opt }}
                                  </el-checkbox>
                                </el-checkbox-group>
                              </div>
                              
                              <!-- 文本域 -->
                              <el-input 
                                v-else-if="element.type === 'textarea'" 
                                type="textarea" 
                                :rows="2"
                                :placeholder="element.placeholder || '请输入多行文本'"
                                readonly
                                class="preview-readonly"
                              />
                            </div>
                          </div>

                          <!-- 元素操作按钮组：仅在编辑模式下显示 -->
                          <div class="element-actions" v-if="isEditMode">
                            <div class="action-btn-list">
                              <el-button 
                                size="small" 
                                :icon="ArrowUp" 
                                title="上移"
                                :disabled="index === 0" 
                                @click="moveElement(tab.key, index, 'up')"
                              />
                              <el-button 
                                size="small" 
                                :icon="ArrowDown" 
                                title="下移"
                                :disabled="index === getCurrentTabElements(tab.key).length - 1" 
                                @click="moveElement(tab.key, index, 'down')"
                              />
                              <el-button 
                                size="small" 
                                type="primary" 
                                plain
                                :icon="EditPen" 
                                title="编辑"
                                @click="openEditElementDialog(tab.key, index)"
                              />
                              <el-button 
                                size="small" 
                                type="danger" 
                                plain
                                :icon="Delete" 
                                title="删除"
                                @click="deleteElement(tab.key, index)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </el-tab-pane>
                </el-tabs>
              </div>
            </el-form>
          </div>
          <div v-else class="empty-state">
            <el-empty description="请先创建或选择一个房屋体检定义项" />
          </div>
        </div>
      </div>
    </el-card>

    <!-- 添加/编辑表单元素对话框 -->
    <el-dialog
      v-model="elementDialogVisible"
      :title="isEditingElement ? '修改表单元素' : '添加表单元素'"
      width="500px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form
        ref="elementFormRef"
        :model="elementForm"
        :rules="elementFormRules"
        label-width="110px"
      >
        <el-form-item label="表单名称" prop="name">
          <el-input v-model="elementForm.name" placeholder="请输入表单名称 (例如: 房屋完损等级)" />
        </el-form-item>
        
        <el-form-item label="表单类型" prop="type">
          <el-select v-model="elementForm.type" placeholder="请选择表单类型" style="width: 100%">
            <el-option label="文本输入框" value="text" />
            <el-option label="数字输入框" value="number" />
            <el-option label="单选选择框" value="radio" />
            <el-option label="多选选择框" value="checkbox" />
            <el-option label="文本域 (多行文本)" value="textarea" />
          </el-select>
        </el-form-item>

        <el-form-item 
          v-if="elementForm.type === 'text' || elementForm.type === 'number' || elementForm.type === 'textarea'" 
          label="预显备注"
        >
          <el-input v-model="elementForm.placeholder" placeholder="输入框内的提示文字，输入值后自动消失" />
        </el-form-item>

        <!-- 单选框需要添加常规选项 -->
        <el-form-item 
          v-if="elementForm.type === 'radio'" 
          label="配置选项" 
          prop="options"
        >
          <div class="options-designer">
            <div 
              v-for="(opt, optIdx) in elementForm.options" 
              :key="'radio_' + optIdx" 
              class="option-input-row"
              draggable="true"
              @dragstart="handleDragStart('normal', optIdx, $event)"
              @dragover.prevent="handleDragOver('normal', optIdx, $event)"
              @dragend="handleDragEnd"
              @drop="handleDrop('normal', optIdx)"
              :class="{ 'is-drag-over': dragGroupType === 'normal' && dragOverIndex === optIdx }"
              style="display: flex; align-items: center; margin-bottom: 8px; width: 100%; transition: all 0.2s;"
            >
              <!-- 拖动手柄 -->
              <el-icon style="cursor: move; margin-right: 8px; color: #94a3b8;" title="按住拖动排序"><Rank /></el-icon>
              
              <el-input 
                v-model="elementForm.options[optIdx]" 
                placeholder="请输入单选选项内容" 
                style="flex: 1; margin-right: 8px;"
              />
              <el-button 
                type="danger" 
                link
                :icon="Delete" 
                title="删除选项"
                @click="removeOptionItem('normal', optIdx)" 
                :disabled="elementForm.options.length <= 1"
              />
            </div>
            <el-button 
              type="primary" 
              plain 
              size="small" 
              :icon="Plus" 
              style="margin-top: 4px;"
              @click="addOptionItem('normal')"
            >
              添加单选选项
            </el-button>
          </div>
        </el-form-item>

        <!-- 多选框需要配置必检和可选项目统计 -->
        <template v-if="elementForm.type === 'checkbox'">
          <el-form-item 
            label="必检项目统计" 
            prop="options"
          >
            <div class="options-designer">
              <div 
                v-for="(opt, optIdx) in elementForm.options" 
                :key="'req_' + optIdx" 
                class="option-input-row"
                draggable="true"
                @dragstart="handleDragStart('req', optIdx, $event)"
                @dragover.prevent="handleDragOver('req', optIdx, $event)"
                @dragend="handleDragEnd"
                @drop="handleDrop('req', optIdx)"
                :class="{ 'is-drag-over': dragGroupType === 'req' && dragOverIndex === optIdx }"
                style="display: flex; align-items: center; margin-bottom: 8px; width: 100%; transition: all 0.2s;"
              >
                <!-- 拖动手柄 -->
                <el-icon style="cursor: move; margin-right: 8px; color: #94a3b8;" title="按住拖动排序"><Rank /></el-icon>
                
                <el-input 
                  v-model="elementForm.options[optIdx]" 
                  placeholder="请输入必检项目内容" 
                  style="flex: 1; margin-right: 8px;"
                />
                <el-button 
                  type="danger" 
                  link
                  :icon="Delete" 
                  title="删除"
                  @click="removeOptionItem('req', optIdx)" 
                  :disabled="elementForm.options.length <= 1"
                />
              </div>
              <el-button 
                type="primary" 
                plain 
                size="small" 
                :icon="Plus" 
                style="margin-top: 4px;"
                @click="addOptionItem('req')"
              >
                添加必检项目
              </el-button>
            </div>
          </el-form-item>

          <el-form-item 
            label="可选项目统计" 
            prop="optionalOptions"
          >
            <div class="options-designer">
              <div 
                v-for="(opt, optIdx) in elementForm.optionalOptions" 
                :key="'opt_' + optIdx" 
                class="option-input-row"
                draggable="true"
                @dragstart="handleDragStart('opt', optIdx, $event)"
                @dragover.prevent="handleDragOver('opt', optIdx, $event)"
                @dragend="handleDragEnd"
                @drop="handleDrop('opt', optIdx)"
                :class="{ 'is-drag-over': dragGroupType === 'opt' && dragOverIndex === optIdx }"
                style="display: flex; align-items: center; margin-bottom: 8px; width: 100%; transition: all 0.2s;"
              >
                <!-- 拖动手柄 -->
                <el-icon style="cursor: move; margin-right: 8px; color: #94a3b8;" title="按住拖动排序"><Rank /></el-icon>
                
                <el-input 
                  v-model="elementForm.optionalOptions[optIdx]" 
                  placeholder="请输入可选项目内容 (不填则不渲染两栏)" 
                  style="flex: 1; margin-right: 8px;"
                />
                <el-button 
                  type="danger" 
                  link
                  :icon="Delete" 
                  title="删除"
                  @click="removeOptionItem('opt', optIdx)" 
                />
              </div>
              <el-button 
                type="primary" 
                plain 
                size="small" 
                :icon="Plus" 
                style="margin-top: 4px;"
                @click="addOptionItem('opt')"
              >
                添加可选项目
              </el-button>
            </div>
          </el-form-item>
        </template>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="elementDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmElementSubmit">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, nextTick, onUnmounted, watchEffect } from 'vue';
import { 
  Plus, Refresh, Edit, Close, DocumentChecked, 
  RefreshLeft, ArrowUp, ArrowDown, EditPen, Delete, Minus, Rank
} from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import DynamicTable from '@/components/DynamicTable.vue';
import { ParameterSettings } from '@/network/index';

// 定义体检定义数据接口
interface HouseCheckElement {
  id: string;
  name: string;
  type: 'text' | 'number' | 'radio' | 'checkbox' | 'textarea';
  options: string[];
  optionalOptions?: string[];
  placeholder?: string;
}

interface HouseCheckData {
  id?: number | string;
  name: string;
  tabsData: Record<string, HouseCheckElement[]>;
}

// 5个固定页签，做成数组循环渲染，方便后续增减更改
const tabList = [
  { key: 'basicInfo', label: '房屋基本信息' },
  { key: 'siteEnv', label: '场地环境及地基基础' },
  { key: 'mainStruct', label: '主体机构' }, // 保持与用户需求“主体机构”字样一致
  { key: 'envelopeSys', label: '围护系统' },
  { key: 'equipment', label: '设备设施及管线' }
];

// 所属类别双向映射
const categoryToTabKey: Record<string, string> = {
  '1房屋基础信息': 'basicInfo',
  '2场地环境及地基基础': 'siteEnv',
  '3主体机构': 'mainStruct',
  '4围护系统': 'envelopeSys',
  '5设备设施及管线': 'equipment'
};

const tabKeyToCategory: Record<string, string> = {
  'basicInfo': '1房屋基础信息',
  'siteEnv': '2场地环境及地基基础',
  'mainStruct': '3主体机构',
  'envelopeSys': '4围护系统',
  'equipment': '5设备设施及管线'
};

// 数据类型双向映射
const typeToBackend: Record<string, string> = {
  'text': '文本',
  'number': '数字',
  'radio': '选项',
  'checkbox': '多选',
  'textarea': '文本域'
};

const backendToType: Record<string, string> = {
  '文本': 'text',
  '数字': 'number',
  '选项': 'radio',
  '多选': 'checkbox',
  '文本域': 'textarea'
};

// 双向转换函数：后端数据结构 -> 前端数据结构
const transformBackendToCheckData = (backendData: any): HouseCheckData => {
  const emptyTabsData: Record<string, HouseCheckElement[]> = {};
  tabList.forEach(tab => {
    emptyTabsData[tab.key] = [];
  });

  const fieldList = backendData.房屋建筑定期体检数据采集模板字段表集合 || [];
  fieldList.forEach((field: any) => {
    const tabKey = categoryToTabKey[field.所属类别] || 'basicInfo';
    emptyTabsData[tabKey].push({
      id: String(field.Id || 'elem_' + Date.now().toString().slice(-6) + Math.random().toString().slice(-2)),
      name: field.字段名称,
      type: (backendToType[field.数据类型] as any) || 'text',
      options: field.必选项 || [],
      optionalOptions: field.可选项 || [],
      placeholder: field.预显备注 || ''
    });
  });

  return {
    id: backendData.Id || '',
    name: backendData.名称 || '',
    tabsData: emptyTabsData
  };
};

// 双向转换函数：前端数据结构 -> 后端数据结构
const transformCheckDataToBackend = (frontendData: HouseCheckData): any => {
  const fieldCollection: any[] = [];
  
  let fieldIndex = 0;
  tabList.forEach(tab => {
    const elements = frontendData.tabsData[tab.key] || [];
    elements.forEach((elem) => {
      fieldCollection.push({
        "字段名称": elem.name,
        "所属类别": tabKeyToCategory[tab.key],
        "数据类型": typeToBackend[elem.type] || '文本',
        "必选项": elem.options || [],
        "必选项JSON": JSON.stringify(elem.options || []),
        "可选项": elem.optionalOptions || [],
        "可选项JSON": JSON.stringify(elem.optionalOptions || []),
        "预显备注": elem.placeholder || '',
        "index": fieldIndex++,
        "Id": isNaN(Number(elem.id)) ? 0 : Number(elem.id)
      });
    });
  });

  return {
    "名称": frontendData.name,
    "最后修改人": "",
    "版本": "1.0",
    "实施日期": new Date().toISOString(),
    "ISDELETED": false,
    "房屋建筑定期体检数据采集模板字段表集合": fieldCollection,
    "Id": isNaN(Number(frontendData.id)) ? 0 : Number(frontendData.id)
  };
};

// 左侧面板拖拽调整宽度相关
const mainContentRef = ref<HTMLElement | null>(null);
const leftPanelRef = ref<HTMLElement | null>(null);
const leftPanelWidth = ref<string>('30%');
const isResizing = ref<boolean>(false);
const minPanelWidth = 220;

const startResize = (e: MouseEvent) => {
  isResizing.value = true;
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';
};

const handleResize = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return;
  const containerRect = mainContentRef.value.getBoundingClientRect();
  const maxWidth = containerRect.width / 2;
  let newWidth = e.clientX - containerRect.left;
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth));
  leftPanelWidth.value = newWidth + 'px';
};

const stopResize = () => {
  isResizing.value = false;
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

// 列表及分页配置
const listLoading = ref<boolean>(false);
const detailLoading = ref<boolean>(false);
const saveLoading = ref<boolean>(false);
const isEditMode = ref<boolean>(false);

const activeTab = ref<string>('basicInfo');

const checkTableModel = ref<Record<string, string>>({
  Id: 'String',
  名称: 'String',
  最后修改人: 'String',
  版本: 'String',
  实施日期: 'String'
});

const checkTableLabels = ref<Record<string, string>>({
  Id: '模板ID',
  名称: '报告模板名称',
  最后修改人: '最后修改人',
  版本: '版本',
  实施日期: '实施日期'
});

const checkPagination = ref({
  page: 1,
  pageSize: 15,
  total: 0,
  show: true
});

// 本次编辑的房屋体检项
const currentCheck = ref<HouseCheckData | null>(null);
const checkFormRef = ref<any>(null);
const checkFormRules = {
  name: [
    { required: true, message: '请输入房屋体检报告名称', trigger: 'blur' }
  ]
};

// 当前选中行 key
const activeCheckRowKey = computed(() => {
  return currentCheck.value?.id ? String(currentCheck.value.id) : '';
});

// 🌟 契合 DynamicTable 与后端列表接口的多重过滤筛选参数
const filterParams = ref<any[]>([]);
const allCheckList = ref<HouseCheckData[]>([]);

// 加载房屋体检定义列表
const loadCheckList = async () => {
  listLoading.value = true;
  try {
    const paginationData = {
      page: checkPagination.value.page,
      intPageSize: checkPagination.value.pageSize
    };
    const res = await ParameterSettings.houseCheckListIndex(
      paginationData,
      filterParams.value
    );
    if (res && res.status === 200 && res.success && res.response) {
      const responseData = Array.isArray(res.response) ? res.response : (res.response.data || []);
      allCheckList.value = responseData.map((item: any) => {
        return {
          id: item.Id || item.id,
          name: item.名称 || item.name,
          Id: item.Id || item.id,
          名称: item.名称 || item.name,
          最后修改人: item.最后修改人 || '-',
          版本: item.版本 || '-',
          实施日期: item.实施日期 ? item.实施日期.replace('T', ' ') : '-',
          tabsData: {}
        };
      });
      if (res.response.model && typeof res.response.model === 'object') {
        checkTableModel.value = res.response.model;
      }
      // 同步后端分页信息
      checkPagination.value.total = res.response.dataCount || 0;
      if (res.response.pageCount) {
        checkPagination.value.page = res.response.page || checkPagination.value.page;
      }
    } else {
      allCheckList.value = [];
    }
  } catch (error) {
    console.error('加载房屋体检定义列表失败:', error);
    ElMessage.error('加载列表失败');
  } finally {
    listLoading.value = false;
  }
};

// 获取某个页签下的表单元素数组
const getCurrentTabElements = (tabKey: string): HouseCheckElement[] => {
  if (!currentCheck.value || !currentCheck.value.tabsData) return [];
  return currentCheck.value.tabsData[tabKey] || [];
};

// 列表筛选
const handleFilter = (filters: any[]) => {
  filterParams.value = filters;
  checkPagination.value.page = 1;
  loadCheckList();
};

// 分页改变
const handleCheckPageChange = (page: number) => {
  checkPagination.value.page = page;
  loadCheckList();
};

const handleCheckPageSizeChange = (pageSize: number) => {
  checkPagination.value.pageSize = pageSize;
  checkPagination.value.page = 1;
  loadCheckList();
};

// 新增房屋体检定义项
const addNewCheck = () => {
  // 生成空的基础结构，初始化 5 个页签的空数组
  const emptyTabsData: Record<string, HouseCheckElement[]> = {};
  tabList.forEach(tab => {
    emptyTabsData[tab.key] = [];
  });

  currentCheck.value = {
    id: '', // 新建无 ID
    name: '新建房屋体检定义_' + (allCheckList.value.length + 1),
    tabsData: emptyTabsData
  };
  
  isEditMode.value = true; // 新增默认进入编辑模式
  activeTab.value = 'basicInfo'; // 默认定位到第一个页签
};

// 查看/编辑体检定义项详情
const handleCheckEdit = async (row: any) => {
  const itemId = row.id || row.Id;
  if (!itemId) return;

  detailLoading.value = true;
  try {
    const res = await ParameterSettings.houseCheckById({ id: itemId });
    if (res && res.status === 200 && res.success && res.response) {
      currentCheck.value = transformBackendToCheckData(res.response);
      isEditMode.value = false; // 默认是查看模式
    } else {
      ElMessage.error('加载详情失败');
    }
  } catch (error) {
    console.error('加载详情失败:', error);
    ElMessage.error('加载详情失败');
  } finally {
    detailLoading.value = false;
  }
};

// 删除体检定义项
const handleCheckDelete = (row: any) => {
  const itemId = row.id || row.Id;
  if (!itemId) return;

  ElMessageBox.confirm(`确定要删除房屋体检定义项 "${row.name}" 吗？`, '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await ParameterSettings.houseCheckDelete({ id: itemId });
      if (res && res.status === 200 && res.success) {
        ElMessage.success('删除成功');
        // 如果删除的是当前选中的，清空右侧详情
        if (currentCheck.value && String(currentCheck.value.id) === String(itemId)) {
          currentCheck.value = null;
          isEditMode.value = false;
        }
        await loadCheckList();
      } else {
        ElMessage.error(res.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      ElMessage.error('删除失败');
    }
  }).catch(() => undefined);
};

// 进入编辑模式
const enterEditMode = () => {
  if (currentCheck.value) {
    // 拷贝一份备份，用来取消编辑时恢复
    (currentCheck.value as any)._backup = JSON.parse(JSON.stringify(currentCheck.value));
    isEditMode.value = true;
  }
};

// 取消编辑并退回查看
const exitEditMode = () => {
  if (currentCheck.value) {
    if ((currentCheck.value as any)._backup) {
      // 还原备份
      const backup = (currentCheck.value as any)._backup;
      currentCheck.value = backup;
      delete (currentCheck.value as any)._backup;
    } else if (!currentCheck.value.id) {
      // 如果是新增状态，直接取消则清空右侧
      currentCheck.value = null;
    }
  }
  isEditMode.value = false;
};

// 重置当前正在编辑的数据
const resetCurrentCheck = () => {
  if (currentCheck.value) {
    if ((currentCheck.value as any)._backup) {
      const backup = (currentCheck.value as any)._backup;
      // 保持 backup，但重置当前值为深拷贝的 backup 副本
      currentCheck.value = JSON.parse(JSON.stringify(backup));
    } else {
      // 新增状态重置
      const emptyTabsData: Record<string, HouseCheckElement[]> = {};
      tabList.forEach(tab => {
        emptyTabsData[tab.key] = [];
      });
      currentCheck.value.name = '';
      currentCheck.value.tabsData = emptyTabsData;
    }
    ElMessage.info('重置成功');
  }
};

// 保存体检定义数据
const saveCheckData = async () => {
  if (!checkFormRef.value) return;
  
  await checkFormRef.value.validate(async (valid: boolean) => {
    if (valid) {
      saveLoading.value = true;
      try {
        const backendPayload = transformCheckDataToBackend(currentCheck.value!);
        const res = await ParameterSettings.houseCheckSave(backendPayload);
        if (res && res.status === 200 && res.success) {
          ElMessage.success('保存成功');
          isEditMode.value = false;
          await loadCheckList(); // 重新加载列表
          
          // 重新加载选中的详情数据
          const savedId = res.response?.Id || res.response?.id || backendPayload.Id;
          if (savedId) {
            const foundItem = allCheckList.value.find(item => String(item.id) === String(savedId));
            if (foundItem) {
              await handleCheckEdit(foundItem);
            } else {
              currentCheck.value = null;
            }
          } else {
            currentCheck.value = null;
          }
        } else {
          ElMessage.error(res.message || '保存失败');
        }
      } catch (e) {
        console.error('保存失败:', e);
        ElMessage.error('保存失败');
      } finally {
        saveLoading.value = false;
      }
    } else {
      ElMessage.warning('请填写必填项后再保存');
    }
  });
};

// ==========================================
// 动态表单设计器相关操作
// ==========================================

const elementDialogVisible = ref<boolean>(false);
const elementFormRef = ref<any>(null);
const isEditingElement = ref<boolean>(false);
const currentEditingTabKey = ref<string>('');
const currentEditingElementIndex = ref<number>(-1);

// 表单元素编辑模型
const elementForm = ref<{
  name: string;
  type: 'text' | 'number' | 'radio' | 'checkbox' | 'textarea';
  options: string[]; // 单选选项/多选必选项
  optionalOptions: string[]; // 多选可选项
  placeholder: string;
}>({
  name: '',
  type: 'text',
  options: ['', ''],
  optionalOptions: [],
  placeholder: ''
});

const elementFormRules = {
  name: [
    { required: true, message: '请输入表单名称', trigger: 'blur' }
  ],
  type: [
    { required: true, message: '请选择表单类型', trigger: 'change' }
  ]
};

// 打开添加表单元素的弹窗
const openAddElementDialog = (tabKey: string) => {
  currentEditingTabKey.value = tabKey;
  isEditingElement.value = false;
  currentEditingElementIndex.value = -1;
  
  elementForm.value = {
    name: '',
    type: 'text',
    options: ['', ''],
    optionalOptions: [],
    placeholder: ''
  };
  
  elementDialogVisible.value = true;
  nextTick(() => {
    if (elementFormRef.value) {
      elementFormRef.value.clearValidate();
    }
  });
};

// 打开编辑已有表单元素的弹窗
const openEditElementDialog = (tabKey: string, index: number) => {
  currentEditingTabKey.value = tabKey;
  isEditingElement.value = true;
  currentEditingElementIndex.value = index;
  
  const element = currentCheck.value!.tabsData[tabKey][index];
  elementForm.value = {
    name: element.name,
    type: element.type,
    options: element.options && element.options.length > 0 ? [...element.options] : ['', ''],
    optionalOptions: element.optionalOptions ? [...element.optionalOptions] : [],
    placeholder: element.placeholder || ''
  };
  
  elementDialogVisible.value = true;
  nextTick(() => {
    if (elementFormRef.value) {
      elementFormRef.value.clearValidate();
    }
  });
};

// 对话框表单添加选项
const addOptionItem = (groupType: 'req' | 'opt' | 'normal') => {
  if (groupType === 'req' || groupType === 'normal') {
    elementForm.value.options.push('');
  } else {
    elementForm.value.optionalOptions.push('');
  }
};

// 对话框表单删除选项
const removeOptionItem = (groupType: 'req' | 'opt' | 'normal', idx: number) => {
  if (groupType === 'req' || groupType === 'normal') {
    if (elementForm.value.options.length > 1) {
      elementForm.value.options.splice(idx, 1);
    }
  } else {
    elementForm.value.optionalOptions.splice(idx, 1);
  }
};

// 🌟 选项 HTML5 原生拖曳排序状态与方法
const dragGroupType = ref<'req' | 'opt' | 'normal' | null>(null);
const dragIndex = ref<number>(-1);
const dragOverIndex = ref<number>(-1);

// 开始拖拽
const handleDragStart = (groupType: 'req' | 'opt' | 'normal', index: number, event: DragEvent) => {
  dragGroupType.value = groupType;
  dragIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
};

// 拖拽悬悬浮划过
const handleDragOver = (groupType: 'req' | 'opt' | 'normal', index: number, event: DragEvent) => {
  if (dragGroupType.value !== groupType) return;
  dragOverIndex.value = index;
  event.preventDefault();
};

// 放置拖动并交换数组项顺序
const handleDrop = (groupType: 'req' | 'opt' | 'normal', targetIndex: number) => {
  if (dragGroupType.value !== groupType || dragIndex.value === -1 || dragIndex.value === targetIndex) {
    return;
  }
  
  const list = groupType === 'opt' ? elementForm.value.optionalOptions : elementForm.value.options;
  // 提取拖曳项
  const dragItem = list[dragIndex.value];
  // 移除
  list.splice(dragIndex.value, 1);
  // 插入
  list.splice(targetIndex, 0, dragItem);
  
  // 重置拖动状态
  dragIndex.value = -1;
  dragOverIndex.value = -1;
  dragGroupType.value = null;
};

// 结束拖拽
const handleDragEnd = () => {
  dragIndex.value = -1;
  dragOverIndex.value = -1;
  dragGroupType.value = null;
};

// 确定提交表单元素（添加或保存修改）
const confirmElementSubmit = async () => {
  if (!elementFormRef.value) return;
  
  await elementFormRef.value.validate((valid: boolean) => {
    if (valid) {
      const tabKey = currentEditingTabKey.value;
      const elements = currentCheck.value!.tabsData[tabKey];
      
      // 处理选项值：如果是单选/多选，过滤空选项
      let finalOptions: string[] = [];
      let finalOptionalOptions: string[] = [];
      if (elementForm.value.type === 'radio' || elementForm.value.type === 'checkbox') {
        finalOptions = elementForm.value.options.map(opt => opt.trim()).filter(opt => opt !== '');
        if (finalOptions.length === 0) {
          ElMessage.warning('常规选项/必检项目至少需要配置一个有效的选项值');
          return;
        }
      }
      if (elementForm.value.type === 'checkbox') {
        finalOptionalOptions = elementForm.value.optionalOptions.map(opt => opt.trim()).filter(opt => opt !== '');
      }
      
      if (isEditingElement.value) {
        // 修改旧元素
        const targetIndex = currentEditingElementIndex.value;
        if (targetIndex >= 0 && elements[targetIndex]) {
          elements[targetIndex].name = elementForm.value.name;
          elements[targetIndex].type = elementForm.value.type;
          elements[targetIndex].options = finalOptions;
          elements[targetIndex].optionalOptions = finalOptionalOptions;
          elements[targetIndex].placeholder = elementForm.value.placeholder;
          ElMessage.success('修改成功');
        }
      } else {
        // 添加新元素
        const newElem: HouseCheckElement = {
          id: 'elem_' + Date.now().toString().slice(-6),
          name: elementForm.value.name,
          type: elementForm.value.type,
          options: finalOptions,
          optionalOptions: finalOptionalOptions,
          placeholder: elementForm.value.placeholder
        };
        elements.push(newElem);
        ElMessage.success('添加成功');
      }
      
      elementDialogVisible.value = false;
    }
  });
};

// 删除单个表单元素
const deleteElement = (tabKey: string, index: number) => {
  const elements = currentCheck.value!.tabsData[tabKey];
  if (elements && elements[index]) {
    const elemName = elements[index].name;
    ElMessageBox.confirm(`确定要删除表单元素 "${elemName}" 吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      elements.splice(index, 1);
      ElMessage.success('表单元素已删除');
    }).catch(() => undefined);
  }
};

// 上下移动排序表单元素
const moveElement = (tabKey: string, index: number, direction: 'up' | 'down') => {
  const elements = currentCheck.value!.tabsData[tabKey];
  if (!elements) return;
  
  if (direction === 'up' && index > 0) {
    // 与上一项交换
    const temp = elements[index];
    elements[index] = elements[index - 1];
    elements[index - 1] = temp;
  } else if (direction === 'down' && index < elements.length - 1) {
    // 与下一项交换
    const temp = elements[index];
    elements[index] = elements[index + 1];
    elements[index + 1] = temp;
  }
};

// 生命周期钩子
onMounted(() => {
  loadCheckList();
});
</script>

<style lang="less" scoped>
.house-check-container {
  height: 100%;
  
  :deep(.el-card) {
    height: 100%;
    .el-card__body {
      height: 100%;
      padding: 0;
    }
  }

  .main-content {
    display: flex;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  // 左侧面板
  .left-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    flex-shrink: 0;
    border-right: 1px solid var(--app-border-color);
    background-color: #fff;
    
    .list-header {
      padding: 12px;
      border-bottom: 1px solid var(--app-border-color);
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .check-list-wrapper {
      flex: 1;
      overflow: hidden;
      padding: 8px;
    }
  }

  // 拖拽手柄
  .resize-handle {
    width: 4px;
    height: 100%;
    cursor: col-resize;
    background-color: transparent;
    transition: background-color 0.2s;
    position: relative;
    z-index: 10;
    flex-shrink: 0;

    &:hover, &:active {
      background-color: #409eff;
    }
  }

  // 右侧编辑区域
  .right-panel {
    flex: 1;
    height: 100%;
    overflow: hidden;
    background-color: var(--app-page-bg, #f7f8fa);
    display: flex;
    flex-direction: column;

    .edit-area {
      flex: 1;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .empty-state {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #fff;
    }
  }

  // 顶部操作栏
  .edit-mode-control {
    padding: 12px 24px;
    background-color: #fff;
    border-bottom: 1px solid var(--app-border-color);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-actions, .right-actions {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .form-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  // 基础名称基本信息区 (预留可扩容区域)
  .basic-info-section {
    padding: 20px 24px 12px 24px;
    background-color: #fff;
    border-bottom: 1px dashed #e4e7ed;

    .check-name-item {
      margin-bottom: 0;
    }
  }

  // 选项循环展示 Tabs
  .tabs-container {
    flex: 1;
    padding: 16px 24px 24px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .design-tabs {
      flex: 1;
      border: 1px solid var(--app-border-color);
      border-radius: 6px;
      box-shadow: none;

      :deep(.el-tabs__item) {
        color: #000000 !important; // 🌟 强力重塑所有页签标题字样为稳重显目的纯黑色
        font-weight: 500;
        
        &.is-active {
          color: #409eff !important; // 🌟 激活项保持经典的高对比主色（蓝色）
          font-weight: 600;
        }
        
        &:hover {
          color: #409eff !important; // 🌟 悬浮项保持淡蓝色反馈
        }
      }

      :deep(.el-tabs__content) {
        max-height: calc(100vh - 290px);
        min-height: 520px;
        overflow-y: auto;
        padding: 20px;
        background-color: #fff;
      }
    }
  }

  // 表单设计器视图样式
  .tab-designer {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;

    .designer-toolbar {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-bottom: 12px;
      border-bottom: 1px dashed #e4e7ed;

      .toolbar-tip {
        font-size: 13px;
        color: #909399;
      }
    }

    .elements-list {
      display: flex;
      flex-direction: column;
      gap: 12px;

      .empty-elements {
        padding: 40px 0;
      }
    }

    // 预览态 readonly 输入框：模拟 disabled 外观但保留 placeholder 可见
    :deep(.preview-readonly) {
      .el-input__inner,
      .el-textarea__inner {
        cursor: not-allowed;
        background-color: #f5f7fa;
        color: transparent;
      }
      .el-input__inner::placeholder,
      .el-textarea__inner::placeholder {
        color: #a8abb2;
      }
    }

    // 预览态数字输入框：disabled 时 placeholder 不可见，用 readonly + 样式模拟
    :deep(.preview-number) {
      &.is-disabled {
        .el-input__inner {
          -webkit-text-fill-color: transparent;
        }
        .el-input__inner::placeholder {
          color: #a8abb2 !important;
          -webkit-text-fill-color: #a8abb2 !important;
        }
      }
    }

    // 每一个表单元素卡片
    .element-item-card {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 10px 14px;
      background-color: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      transition: all 0.2s ease-in-out;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.02);

      &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
      }

      &.is-edit-mode:hover {
        border-color: #3b82f6;
        background-color: #f8fafc;
      }

      .element-preview-area {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;

        .element-label {
          width: 100px;
          font-size: 13px;
          color: #475569;
          font-weight: 500;
          text-align: right;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex-shrink: 0;

          &::after {
            content: ":";
          }
        }

        .element-control-wrapper {
          flex: 1;
          max-width: 100%;
          min-width: 0;
          overflow-wrap: anywhere;
          
          :deep(.el-input.is-disabled .el-input__inner) {
            color: #1e293b;
            -webkit-text-fill-color: #1e293b;
            cursor: default;
          }

          :deep(.el-textarea.is-disabled .el-textarea__inner) {
            color: #1e293b;
            -webkit-text-fill-color: #1e293b;
            cursor: default;
          }

          :deep(.el-radio.is-disabled) {
            cursor: default;
            .el-radio__label {
              color: #1e293b;
            }
          }

          :deep(.el-checkbox.is-disabled) {
            cursor: default;
            .el-checkbox__label {
              color: #1e293b;
            }
          }

          :deep(.el-radio-group),
          :deep(.el-checkbox-group) {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: 100%;
            min-width: 0;
          }

          :deep(.el-radio),
          :deep(.el-checkbox) {
            display: flex;
            align-items: flex-start;
            height: auto;
            min-height: 22px;
            margin-right: 0;
            white-space: normal;
            max-width: 100%;
            min-width: 0;
          }

          :deep(.el-radio__input),
          :deep(.el-checkbox__input) {
            flex-shrink: 0;
            margin-top: 4px;
          }

          :deep(.el-radio__label),
          :deep(.el-checkbox__label) {
            flex: 1;
            min-width: 0;
            line-height: 1.5;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          // 🌟 必检 / 可选项目统计两栏对照表格
          .checkbox-split-view {
            display: flex;
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            overflow: hidden;
            width: 100%;
            background-color: #f8fafc;
            
            .split-column {
              flex: 1;
              display: flex;
              flex-direction: column;
              min-width: 0;
              
              &:first-child {
                border-right: 1px solid #cbd5e1;
              }
              
              .column-header {
                padding: 6px 12px;
                background-color: #f1f5f9;
                font-weight: 600;
                font-size: 12px;
                color: #475569;
                border-bottom: 1px solid #cbd5e1;
                text-align: center;
              }
              
              .column-body {
                padding: 12px;
                display: flex;
                flex-direction: column;
                gap: 8px;
                background-color: #fff;
                
                :deep(.el-checkbox-group) {
                  display: flex;
                  flex-direction: column;
                  gap: 8px;
                }

                :deep(.el-checkbox) {
                  margin-bottom: 0;
                  margin-right: 0;
                  display: flex;
                  align-items: flex-start;
                  white-space: normal;
                  height: auto;
                  
                  .el-checkbox__label {
                    line-height: 1.4;
                    padding-left: 8px;
                    color: #1e293b;
                  }
                }
              }
            }
          }
        }
      }

      .element-actions {
        flex-shrink: 0;
        
        .action-btn-list {
          display: flex;
          gap: 6px;
          align-items: center;
        }
      }
    }
  }

  // 弹窗中动态设计选项配置
  .options-designer {
    border: 1px solid #e4e7ed;
    padding: 12px;
    border-radius: 4px;
    background-color: #fafbfc;

    .option-input-row {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      padding: 4px;
      border: 1px solid transparent;
      border-radius: 4px;
      transition: all 0.2s;

      &.is-drag-over {
        border-color: #409eff;
        background-color: #ecf5ff;
        border-style: dashed;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
